import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useContext,
  useRef,
} from 'react';
import classNames from 'classnames';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { Button, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import InputField from 'components/InputField';
import validations from 'validations';
import TextAreaField from 'components/TextAreaField';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { sentryError } from 'utils';
import HandIcon from 'icons/Hand.icon';
import ListingContext from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/providers/Listing/Listing.context';
import SelectField from 'components/SelectField';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useUserBasicInfo from 'hooks/useUserBasicInfo';
import ContactNumberField from 'components/ContactNumberField';
import EmailField from 'components/EmailField';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';
import mixpanelService from 'services/mixpanelService';

import './InviteListingViewer.styles.scss';

type InviteListingViewerProps = {
  className?: string;
  listingId: string;
  isPrivate: boolean;
  onAddLead: () => void;
};

const InviteListingViewer: React.ForwardRefRenderFunction<
  OverlayRef,
  InviteListingViewerProps
> = (props, ref) => {
  const { className, listingId, isPrivate, onAddLead } = props;

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    dealRoom: { _id: dealId, name: dealName, bossModeReferrerOptions },
    setEnquiry,
  } = useContext(ListingContext);

  const formRef = useRef(null);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('invite-listing-viewer', className);

  const {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    onEmailChange,
    resetState,
  } = useUserBasicInfo();

  const handleOnSubmit = useCallback(
    async (values) => {
      try {
        mixpanelService.enquiryCreationInitiated(
          getMixpanelProps({
            dealId,
            dealName,
            enquiryOrigin: ENQUIRY_ORIGIN.MANUAL,
          }) as EnquiryCreationInitiatedEventProps,
        );

        const { data } = await api.dealRoom.invitePrivateViewer(
          listingId,
          values,
        );
        setEnquiry(data);
        popSuccess(t('successfullyInvitedPotentialLead'));
        modal.close();
      } catch (e) {
        sentryError(e);
        popServerError(e);
      }
    },
    [
      dealName,
      dealId,
      listingId,
      modal,
      getMixpanelProps,
      popServerError,
      popSuccess,
      setEnquiry,
      t,
    ],
  );

  if (!isPrivate) return null;

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('invitePotentialLeads')}
      onClose={resetState}
    >
      <Form
        onSubmit={handleOnSubmit}
        render={({ handleSubmit, submitting, valid, form, values }) => {
          formRef.current = values;

          return (
            <form
              onSubmit={handleSubmit}
              className="invite-listing-viewer__form"
            >
              <div className="banner">
                <p className="title">
                  <HandIcon />
                  {t('inviteListingViewerBannerTitle')}
                </p>
                <p className="description">
                  {t('inviteListingViewerBannerDescription')}
                  <span onClick={onAddLead}>{t('addThemAsEnquiry')}</span>
                </p>
              </div>
              <p>{t('invitePotentialLeadsDescription')}</p>
              <div className="invite-listing-viewer__form__row">
                <Field
                  name="firstName"
                  label={t('firstName')}
                  placeholder={t('firstName')}
                  component={InputField}
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                  disabled={isUserBasicInfoSet}
                  required
                />
                <Field
                  name="lastName"
                  label={t('lastName')}
                  placeholder={t('lastName')}
                  component={InputField}
                  required
                  validate={validations.composeValidators(
                    validations.required(),
                    validations.noLongerThan(
                      50,
                      t('noLongerThan', { length: 50 }),
                    ),
                    validations.fullNameValidation(
                      t('specialCharactersOrNumbersNotAllowed'),
                    ),
                  )}
                  disabled={isUserBasicInfoSet}
                />
              </div>
              <div className="invite-listing-viewer__form__row">
                <EmailField
                  required
                  onChangeHandle={(email) => onEmailChange({ email, form })}
                />
                <ContactNumberField />
              </div>

              <Field
                label={t('petsonalMessageToThisPerson')}
                name="notesAboutEnquiry"
                description={t('thisOneIsOptional')}
                component={TextAreaField}
                maxLength={500}
              />
              {isSuperAdmin && (
                <Field
                  label={t('whoAddedThisLead')}
                  component={SelectField}
                  options={bossModeReferrerOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  name="referrerName"
                />
              )}
              <Button
                type="submit"
                disabled={submitting || !valid || isFetchingUserBasicInfo}
              >
                {t('inviteAPrivateViewing')}
              </Button>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(InviteListingViewer);
